import { scan } from 'react-scan';
import React, { useEffect } from 'react';

// ** Next
import Head from 'next/head';
import { Router } from 'next/router';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { SpeedInsights } from '@vercel/speed-insights/next';

// ** Loader
import NProgress from 'nprogress';

// ** i18n
import i18next from 'i18next';

// ** Emotion
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

// Create emotion cache with consistent key
const cache = createCache({
  key: 'css',
  prepend: true,
});

// ** MUI
import { LicenseInfo } from '@mui/x-license';

// ** Config
import '@/config/i18n';
import initPackages from '@/initPackages';
import registerThemeConfig from '@/config/theme';
import themeConfig from '@erikdakoda/site-template-ui/config';
import registerAuthConfig from '@/config/auth';

// ** Components
import UserLayout from '@/components/UserLayout';
import ThemeComponent from '@erikdakoda/site-template-ui/theme/ThemeComponent';
import { Analytics } from '@vercel/analytics/react';
import { type ConfirmOptions } from '@erikdakoda/confirm-ui/types';
import { ConfirmProvider } from '@erikdakoda/confirm-ui/components/ConfirmProvider';
import Guard from '@erikdakoda/auth-ui/components/Guard';
import SentryUserSync from '@erikdakoda/auth-ui/components/SentryUserSync';
import StorageUserSync from '@erikdakoda/auth-ui/components/StorageUserSync';
import { AnalyticsProvider } from '@erikdakoda/analytics/client/AnalyticsProvider';

// ** Contexts
import {
  SettingsConsumer,
  SettingsProvider,
} from '@erikdakoda/site-template-ui/context/settingsContext';
import { snackProviderProps } from '@erikdakoda/ui/shared/snackProviderProps';
import { SnackbarProvider } from 'notistack';
import { QuerySettingsProvider as ZenStackHooksProvider } from '@erikdakoda/database/hooks';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// ** Utils
import { handleMutationError, handleQueryError } from '@erikdakoda/ui/shared/queryErrorReporting';

// ** Global css styles
import '@/styles/globals.css';

/*
 * Register MUI X Pro license
 */
LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_X_PRO_LICENSE ?? '');

/*
 * Register configurations
 */
initPackages();
registerAuthConfig();
registerThemeConfig();

/*
 * Configure confirm dialogs
 */
const confirmOptions: ConfirmOptions = {
  titleProps: {
    variant: 'h3',
  },
  confirmationText: i18next.t('uvilo-ai:confirmOk'),
  cancellationText: i18next.t('uvilo-ai:confirmCancel'),
  confirmationButtonProps: {
    autoFocus: true,
    type: 'submit',
    variant: 'contained',
  },
};

/*
 * Configure pace loader
 */
if (themeConfig.config.routingLoader) {
  Router.events.on('routeChangeStart', () => {
    NProgress.start();
  });
  Router.events.on('routeChangeError', () => {
    NProgress.done();
  });
  Router.events.on('routeChangeComplete', () => {
    NProgress.done();
  });
}

/*
 * Extend App Props with Emotion
 */
export type ExtendedAppProps = AppProps & {
  Component: NextPage;
};

/*
 * Create QueryClient outside component to ensure it's available during SSR/prerendering
 */
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
  defaultOptions: {
    queries: {
      // If data is loaded in getServerSideProps avoid refetching immediately on the client
      staleTime: 60 * 1000,
    },
  },
});

const App = (props: ExtendedAppProps) => {
  useEffect(() => {
    scan({
      showToolbar: process.env.NEXT_PUBLIC_REACT_SCAN === 'true',
      enabled: false,
    });
  }, []);

  const { Component, pageProps } = props;

  // Variables
  const contentHeightFixed = Component.contentHeightFixed ?? false;
  const noPadding = Component.noPadding ?? false;
  const contentAlign = Component.contentAlign ?? 'stretch';
  const getLayout =
    Component.getLayout ??
    ((page) => (
      <UserLayout
        contentHeightFixed={contentHeightFixed}
        noPadding={noPadding}
        contentAlign={contentAlign}
      >
        {page}
      </UserLayout>
    ));

  const getPageSettings = Component.getPageSettings ?? undefined;
  const authGuard = Component.authGuard ?? true;
  const guestGuard = Component.guestGuard ?? false;

  return (
    <>
      <AnalyticsProvider>
        <QueryClientProvider client={queryClient}>
          <SentryUserSync />
          <StorageUserSync />
          <ZenStackHooksProvider value={{ endpoint: '/api/model' }}>
            <CacheProvider value={cache}>
              <Head>
                <title>{`${process.env.NEXT_PUBLIC_APP_NAME} - ${process.env.NEXT_PUBLIC_APP_SLOGAN}`}</title>
                <meta
                  name="description"
                  content={`${process.env.NEXT_PUBLIC_APP_NAME} – ${process.env.NEXT_PUBLIC_APP_SLOGAN}`}
                />
                <meta
                  name="keywords"
                  content="AI, ChatGPT, OpenAI, Anthropic, Claude, coach, coaching, habits, uvilo"
                />
                <meta
                  name="viewport"
                  content="user-scalable=no, initial-scale=1, maximum-scale=1, minimum-scale=1, width=device-width, viewport-fit=cover"
                />
              </Head>

              <SettingsProvider {...(getPageSettings ? { pageSettings: getPageSettings() } : {})}>
                <SettingsConsumer>
                  {({ settings }) => {
                    return (
                      <ThemeComponent settings={settings}>
                        <ConfirmProvider defaultOptions={confirmOptions}>
                          <Guard guestGuard={guestGuard} authGuard={authGuard}>
                            <SnackbarProvider {...snackProviderProps}>
                              {getLayout(<Component {...pageProps} />)}
                              {process.env.NEXT_PUBLIC_REACT_QUERY_DEVTOOLS === 'true' && (
                                <ReactQueryDevtools
                                  initialIsOpen={false}
                                  buttonPosition="bottom-right"
                                />
                              )}
                            </SnackbarProvider>
                          </Guard>
                        </ConfirmProvider>
                      </ThemeComponent>
                    );
                  }}
                </SettingsConsumer>
              </SettingsProvider>
            </CacheProvider>
          </ZenStackHooksProvider>
        </QueryClientProvider>
      </AnalyticsProvider>
      <Analytics />
      <SpeedInsights />
    </>
  );
};

export default App;
